import type { ContentBlurb, Criteria, InitialPropsContext, Job } from 'types';

type ContentBlurbOptions = {
  cleanContent?: boolean;
};

export const removeBlurbHTML = (content: string) => {
  if (!content) return '';

  const toReplace = /(<section><p>|<\/p>(\s+)?<\/section>)/g;

  return content.replace(toReplace, '');
};

export const convertToPureBlurbs = (content: string) => {
  const paragraphsRegex = /(?<=<p>)(.*?)(?=<\/p>)/gm;

  return content.match(paragraphsRegex) ?? [];
};

export function getContentBlurb(
  data: ContentBlurb[] | Record<string, Partial<ContentBlurb>>,
  subtype: ContentBlurb['post_subtype'],
  options: ContentBlurbOptions
): ContentBlurb['content'];
export function getContentBlurb(
  data: ContentBlurb[] | Record<string, Partial<ContentBlurb>>,
  subtype: ContentBlurb['post_subtype'][],
  options: ContentBlurbOptions
): ContentBlurb['content'][];
export function getContentBlurb(
  data: ContentBlurb[] | Record<string, Partial<ContentBlurb>>,
  subtype: ContentBlurb['post_subtype'] | ContentBlurb['post_subtype'][],
  options: ContentBlurbOptions
) {
  const cleanContent = options?.cleanContent || false;

  const getContentByType = (type: ContentBlurb['post_subtype']) => {
    const blurb = Array.isArray(data)
      ? data?.find((item) => item.post_subtype === type)
      : data?.[type];
    const { content = '' } = blurb || {};

    return cleanContent ? removeBlurbHTML(content) : content;
  };

  if (Array.isArray(subtype)) {
    return subtype.map(getContentByType);
  }

  return getContentByType(subtype);
}

export const getInitialProps = <P = Record<string, unknown>>(ctx: InitialPropsContext<P>) => {
  const { req, res } = ctx;

  return {
    data: res?.data || null,
    user: req?.user || null,
    locals: res?.locals || null,
  };
};

export const getCppJobCardLocation = (
  isRemote: boolean,
  location: string,
  companyName: string,
  isMatch: boolean
) => {
  if (!isRemote && !location) return '';

  if (!isMatch) {
    if (location) {
      const locationString = `${location} Job`;
      return isRemote ? `Remote or ${locationString}` : locationString;
    } else if (isRemote) {
      return `Remote Job`;
    }
  }

  if (location) {
    const locationString = `${companyName} Job In ${location}`;

    return isRemote ? `${locationString} Or Remote` : locationString;
  } else if (isRemote) {
    return `Remote ${companyName} Job`;
  }
};

/**
 * Purpose: Changes the data argument into an array if not already an array and return it.
 * Also handles falsy values of data argument
 * It returns an empty array if the data contains falsy value (empty string, null or undefined).
 */
export const toArray = <T>(data: T | T[]) => {
  if (!data) return [];
  return Array.isArray(data) ? data : [data];
};

export const getIndexByWeights = (weights: number[]): number => {
  const weightsMap = new Map();

  let sum = 0;
  weights.forEach((w, idx) => {
    sum += w;
    weightsMap.set(sum, idx);
  });

  const index = Math.floor(Math.random() * sum);
  for (const key of weightsMap.keys()) {
    if (index < key) {
      return weightsMap.get(key);
    }
  }
};

export const urlFragment = (url: string) => url?.split('#')?.[1] ?? '';

export const getJobSnippets = (job: Job, criteria: Criteria) => {
  if (!job) return '';
  if (!criteria) return job.jobDescription || '';

  const { titleSnippets = [], jobDescription, companySnippets = [], locationSnippets = [] } = job;

  // cluster pages
  const isCareerCluster = !!(criteria.title && !criteria.state && !criteria.companyID);
  const isCityCluster = !!(!criteria.title && criteria.state && !criteria.companyID);
  const isCompanyCluster = !!(!criteria.title && !criteria.state && criteria.companyID);

  // combo pages
  const isTitleCityCombo = !!(criteria.title && criteria.state && !criteria.companyID);
  const isCompanyTitleCombo = !!(criteria.title && !criteria.state && criteria.companyID);
  const isCompanyCityCombo = !!(!criteria.title && criteria.state && criteria.companyID);

  let snippets = [];

  if (isCareerCluster) {
    snippets = titleSnippets;
  } else if (isCityCluster) {
    snippets = locationSnippets;
  } else if (isCompanyCluster) {
    snippets = companySnippets;
  } else if (isTitleCityCombo) {
    snippets = titleSnippets?.length ? titleSnippets : locationSnippets;
  } else if (isCompanyTitleCombo) {
    snippets = companySnippets?.length ? companySnippets : titleSnippets;
  } else if (isCompanyCityCombo) {
    snippets = companySnippets?.length ? companySnippets : locationSnippets;
  }

  return snippets.filter(Boolean).join(' ').trim() || jobDescription || '';
};
